import { Box, Flex } from '@mantine/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { clampRunHistorySidebarWidth } from './runHistorySidebarSizing.ts';

type DragState = {
  pointerId: number;
  startClientX: number;
  startWidth: number;
};

type RunHistoryResizeHandleProps = {
  width: number;
  minimumWidth: number;
  maximumWidth: number;
  onWidthChange: (width: number) => void;
};

const KEYBOARD_RESIZE_STEP = 16;
const RESIZE_HANDLE_HIT_TARGET_WIDTH = 12;

export function RunHistoryResizeHandle({
  width,
  minimumWidth,
  maximumWidth,
  onWidthChange,
}: RunHistoryResizeHandleProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isFocusVisible, setIsFocusVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const handleRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<DragState | null>(null);
  const bodyStylesRef = useRef<{ cursor: string; userSelect: string } | null>(null);

  const restoreBodyStyles = useCallback(() => {
    const bodyStyles = bodyStylesRef.current;
    if (!bodyStyles) return;

    document.body.style.cursor = bodyStyles.cursor;
    document.body.style.userSelect = bodyStyles.userSelect;
    bodyStylesRef.current = null;
  }, []);

  const stopDragging = useCallback(() => {
    const dragState = dragStateRef.current;
    dragStateRef.current = null;
    if (dragState && handleRef.current?.hasPointerCapture?.(dragState.pointerId)) {
      handleRef.current.releasePointerCapture?.(dragState.pointerId);
    }
    setIsDragging(false);
    restoreBodyStyles();
  }, [restoreBodyStyles]);

  useEffect(
    () => () => {
      dragStateRef.current = null;
      restoreBodyStyles();
    },
    [restoreBodyStyles],
  );

  const updateWidthFromClientX = (clientX: number) => {
    const dragState = dragStateRef.current;
    if (!dragState) return;

    onWidthChange(
      clampRunHistorySidebarWidth(
        dragState.startWidth + clientX - dragState.startClientX,
        minimumWidth,
        maximumWidth,
      ),
    );
  };

  const isActive = isHovered && !isDragging;

  return (
    <Flex
      ref={handleRef}
      role="separator"
      aria-label="Resize run history"
      aria-orientation="vertical"
      aria-valuemin={minimumWidth}
      aria-valuemax={maximumWidth}
      aria-valuenow={width}
      aria-valuetext={`${width}px`}
      tabIndex={0}
      w={RESIZE_HANDLE_HIT_TARGET_WIDTH}
      h="100%"
      justify="center"
      onPointerDown={(event) => {
        if (event.button !== 0 || dragStateRef.current) return;

        event.preventDefault();
        setIsFocusVisible(false);
        event.currentTarget.focus();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        dragStateRef.current = {
          pointerId: event.pointerId,
          startClientX: event.clientX,
          startWidth: width,
        };
        bodyStylesRef.current = {
          cursor: document.body.style.cursor,
          userSelect: document.body.style.userSelect,
        };
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        setIsDragging(true);
      }}
      onPointerMove={(event) => {
        if (dragStateRef.current?.pointerId !== event.pointerId) return;
        updateWidthFromClientX(event.clientX);
      }}
      onPointerUp={(event) => {
        if (dragStateRef.current?.pointerId !== event.pointerId) return;
        stopDragging();
      }}
      onPointerCancel={(event) => {
        if (dragStateRef.current?.pointerId !== event.pointerId) return;
        stopDragging();
      }}
      onLostPointerCapture={(event) => {
        if (dragStateRef.current?.pointerId !== event.pointerId) return;
        stopDragging();
      }}
      onPointerEnter={() => setIsHovered(true)}
      onPointerLeave={() => setIsHovered(false)}
      onFocus={(event) => setIsFocusVisible(event.currentTarget.matches(':focus-visible'))}
      onBlur={() => setIsFocusVisible(false)}
      onKeyDown={(event) => {
        setIsFocusVisible(true);

        if (event.key === 'Escape' && dragStateRef.current) {
          event.preventDefault();
          onWidthChange(dragStateRef.current.startWidth);
          stopDragging();
          return;
        }

        const keyboardWidth = {
          ArrowLeft: width - KEYBOARD_RESIZE_STEP,
          ArrowRight: width + KEYBOARD_RESIZE_STEP,
          Home: minimumWidth,
          End: maximumWidth,
        }[event.key];

        if (keyboardWidth === undefined) return;

        event.preventDefault();
        onWidthChange(clampRunHistorySidebarWidth(keyboardWidth, minimumWidth, maximumWidth));
      }}
      style={{
        cursor: 'col-resize',
        flexShrink: 0,
        marginLeft: -RESIZE_HANDLE_HIT_TARGET_WIDTH / 2,
        marginRight: -RESIZE_HANDLE_HIT_TARGET_WIDTH / 2,
        outline: isFocusVisible ? '2px solid #228be6' : 'none',
        outlineOffset: '-2px',
        touchAction: 'none',
        zIndex: 1,
      }}
    >
      <Box w={isActive ? 2 : 1} h="100%" bg={isActive ? '#228be6' : '#e0e0e0'} />
    </Flex>
  );
}
